import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { CalendarIcon, Pencil, Trash, CalendarDays, Plus } from "lucide-react";
import { useAuth } from "@/contexts/auth";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner"
;


interface Holiday {
  id: string;
  date: string;
  name: string;
  is_public: boolean;
  created_by: string;
  created_at: string;
}

export function HolidaySettings() {
  const { user } = useAuth();
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [, setLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentHoliday, setCurrentHoliday] = useState<Partial<Holiday>>({});
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const fetchHolidays = useCallback(async () => {
    if (!user?.companyId) {
      toast.error("No company ID associated with your account");
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setFetchError(null);
      const startDate = new Date();
      startDate.setFullYear(startDate.getFullYear() - 1); // 1 year ago
      const endDate = new Date();
      endDate.setFullYear(endDate.getFullYear() + 1); // 1 year into future
      
      const { data: rpcData, error: rpcError } = await (supabase as any)
        .rpc('get_company_holidays', { 
          company_id_param: user.companyId,
          start_date: format(startDate, 'yyyy-MM-dd'),
          end_date: format(endDate, 'yyyy-MM-dd')
        });
      if (rpcError) {
        const { data: directData, error: directError } = await supabase
          .from('holidays')
          .select('*')
          .eq('company_id', user.companyId)
          .gte('date', format(startDate, 'yyyy-MM-dd'))
          .lte('date', format(endDate, 'yyyy-MM-dd'))
          .order('date');
        if (directError) {
          toast.error("Failed to load holidays");
          setFetchError(`Direct query error: ${directError.message}`);
          setLoading(false);
          return;
        }
        setHolidays(directData || []);
      } else {
        setHolidays(rpcData || []);
      }
      
      
    } catch (fetchErr) {
      const errorMessage = fetchErr instanceof Error ? fetchErr.message : String(fetchErr);
      toast.error("An unexpected error occurred");
      setFetchError(`Exception: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  }, [user, setLoading, setFetchError, setHolidays]);

  // Add useEffect to fetch holidays when component mounts
  useEffect(() => {
    fetchHolidays();
  }, [user?.companyId, fetchHolidays]); // Re-fetch when company ID changes or fetchHolidays changes

  const handleAddHoliday = () => {
    setCurrentHoliday({});
    setSelectedDate(undefined);
    setIsEditing(false);
    setIsDialogOpen(true);
  };

  const handleEditHoliday = (holiday: Holiday) => {
    setCurrentHoliday(holiday);
    setSelectedDate(new Date(holiday.date));
    setIsEditing(true);
    setIsDialogOpen(true);
  };

  const handleDeleteHoliday = async (id: string) => {
    if (!user?.companyId) return;

    try {
      const { error } = await supabase
        .from('holidays')
        .delete()
        .eq('id', id)
        .eq('company_id', user.companyId);

      if (error) {
        toast.error("Failed to delete holiday");
      } else {
        toast.success("Holiday deleted successfully");
        fetchHolidays();
      }
    } catch {
      toast.error("An unexpected error occurred");
    }
  };

  const handleSaveHoliday = async () => {
    if (!user?.companyId || !selectedDate || !currentHoliday.name) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const holidayData = {
        name: currentHoliday.name,
        date: format(selectedDate, 'yyyy-MM-dd'),
        company_id: user.companyId,
        is_public: true,
        created_by: user.id
      };

      let response;
      if (isEditing && currentHoliday.id) {
        response = await (supabase as any)
          .from('holidays')
          .update(holidayData)
          .eq('id', currentHoliday.id)
          .select();
      } else {
        response = await (supabase as any)
          .from('holidays')
          .insert(holidayData)
          .select();
      }

      if (response.error) {
        toast.error("Failed to save holiday");
      } else {
        toast.success(isEditing ? "Holiday updated successfully" : "Holiday added successfully");
        setIsDialogOpen(false);
        fetchHolidays();
      }
    } catch {
      toast.error("An unexpected error occurred");
    }
  };

  return (
    <Card className="shadow-sm border-border/50">
      <CardHeader className="pb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <CalendarDays className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl font-semibold text-foreground">Company Holidays</CardTitle>
            </div>
          </div>
          <Button 
            onClick={handleAddHoliday}
            size="default"
            variant="secondary"
            className="px-4 h-10"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Holiday
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {fetchError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
            <strong>Error fetching holidays:</strong> {fetchError}
          </div>
        )}
        
        <div className="space-y-4">
          <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
            <span>Total Holidays: {holidays.length}</span>
            {holidays.length > 0 && (
              <span className="text-xs bg-muted px-2 py-1 rounded-md">
                {holidays.filter(h => new Date(h.date) >= new Date()).length} Upcoming
              </span>
            )}
          </div>
          
          {holidays.length === 0 ? (
            <div className="py-12 text-center">
              <CalendarDays className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground text-lg mb-2">No holidays configured yet</p>
              <p className="text-muted-foreground text-sm">Click 'Add Holiday' to create your first company holiday</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-semibold">Holiday Name</TableHead>
                    <TableHead className="font-semibold">Date</TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {holidays.map((holiday) => (
                    <TableRow key={holiday.id} className="hover:bg-muted/30 transition-colors">
                      <TableCell className="font-medium">{holiday.name}</TableCell>
                      <TableCell className="text-muted-foreground">{format(new Date(holiday.date), 'PPP')}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleEditHoliday(holiday)}
                            className="h-8 w-8 hover:bg-primary/10"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleDeleteHoliday(holiday.id)}
                            className="h-8 w-8 hover:bg-destructive/10 text-destructive"
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </CardContent>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader className="pb-4">
            <DialogTitle className="text-xl">
              {isEditing ? "Edit Holiday" : "Add New Holiday"}
            </DialogTitle>
            <DialogDescription className="text-base">
              {isEditing ? "Update the holiday information below." : "Add a new holiday to your company calendar."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="space-y-3">
              <label htmlFor="holidayName" className="text-sm font-medium">Holiday Name</label>
              <Input 
                id="holidayName" 
                value={currentHoliday.name || ''} 
                onChange={(e) => setCurrentHoliday({...currentHoliday, name: e.target.value})}
                placeholder="Enter holiday name"
                className="h-11"
              />
            </div>
            <div className="space-y-3">
              <label className="text-sm font-medium">Holiday Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal h-11"
                  >
                    {selectedDate ? (
                      format(selectedDate, 'PPP')
                    ) : (
                      <span className="text-muted-foreground">Pick a date</span>
                    )}
                    <CalendarIcon className="ml-auto h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="px-6">
              Cancel
            </Button>
            <Button onClick={handleSaveHoliday} variant="secondary" className="px-6">
              {isEditing ? "Update" : "Add"} Holiday
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default HolidaySettings;
